<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import DefaultModal from '@/components/ui/DefaultModal.vue'
import RepairFileStatusMacroConstant from '@/constants/repair-module/repair-file/repairFileStatusMacroConstant'
import DeliveryAddressTypeConstant from '@/constants/transport/deliveryAddressTypeConstant'

const KEY_ACCOUNT_REPAIR_FILE_TRANSPORT_STEP = 'STEP_6'

const props = defineProps({
  repairFile: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['closeModal'])
const { t } = useI18n()
const acknowledged = ref(false)

const returnAddress = computed(() => {
  if (
    props.repairFile.step !== KEY_ACCOUNT_REPAIR_FILE_TRANSPORT_STEP ||
    props.repairFile.status_macro !== RepairFileStatusMacroConstant.TRANSPORT_GO
  ) {
    return null
  }

  return props.repairFile.delivery_address_list?.find(
    (address) => address.type === DeliveryAddressTypeConstant.RETURN
  )
})

const shouldShowReturnAddress = computed(() => Boolean(returnAddress.value))

const addressStreetLine = computed(() =>
  [returnAddress.value?.address_number, returnAddress.value?.address_street]
    .filter(Boolean)
    .join(' ')
)

const addressCityLine = computed(() =>
  [returnAddress.value?.address_zip_code, returnAddress.value?.address_city]
    .filter(Boolean)
    .join(' ')
)

function displayValue(value) {
  return value || t('COMMON.NO_VALUE')
}
</script>

<template>
  <DefaultModal
    :title="t('KEY_ACCOUNT_REPAIR_FILE_OPENING_WARNING.TITLE')"
    :validate-disabled="!acknowledged"
    @close-modal="emit('closeModal', $event)"
  >
    <p class="text-body-1 font-weight-medium mb-3">
      {{ t('KEY_ACCOUNT_REPAIR_FILE_OPENING_WARNING.INTRO') }}
    </p>

    <ol class="warning-list text-body-2 mb-4">
      <li>
        {{ t('KEY_ACCOUNT_REPAIR_FILE_OPENING_WARNING.BILLING_REMINDER') }}
      </li>
      <li>
        <p class="mb-2">
          {{ t('KEY_ACCOUNT_REPAIR_FILE_OPENING_WARNING.REFERENCE_REMINDER') }}
        </p>
        <p class="font-weight-bold mb-1">
          {{ t('KEY_ACCOUNT_REPAIR_FILE_OPENING_WARNING.CALLBACK_REF') }}
          {{ displayValue(repairFile.internal_command_number) }}
        </p>
        <p class="font-weight-bold mb-0">
          {{ t('KEY_ACCOUNT_REPAIR_FILE_OPENING_WARNING.WORKSITE_CODE') }}
          {{ displayValue(repairFile.worksite_code) }}
        </p>
      </li>
      <li v-if="shouldShowReturnAddress">
        <p class="mb-2">
          {{ t('KEY_ACCOUNT_REPAIR_FILE_OPENING_WARNING.DELIVERY_REMINDER') }}
        </p>
        <p class="font-weight-bold mb-1">
          {{ displayValue(returnAddress.address_name) }}
        </p>
        <p v-if="addressStreetLine" class="mb-1">
          {{ addressStreetLine }}
        </p>
        <p v-if="returnAddress.address_complement_1" class="mb-1">
          {{ returnAddress.address_complement_1 }}
        </p>
        <p v-if="returnAddress.address_complement_2" class="mb-1">
          {{ returnAddress.address_complement_2 }}
        </p>
        <p v-if="addressCityLine" class="mb-0">
          {{ addressCityLine }}
        </p>
      </li>
    </ol>

    <v-checkbox
      v-model="acknowledged"
      :label="t('KEY_ACCOUNT_REPAIR_FILE_OPENING_WARNING.ACKNOWLEDGEMENT')"
      class="mb-4"
      density="compact"
      hide-details
    />
  </DefaultModal>
</template>

<style scoped>
.warning-list {
  padding-left: 1.25rem;
}

.warning-list li {
  margin-bottom: 1rem;
}

.warning-list li:last-child {
  margin-bottom: 0;
}
</style>
